import { useState, useEffect, useCallback, useRef } from "react";
import api from "@/lib/api";
import { Plus, Search, TrendingDown, Paperclip, Upload, X, FileText, Eye, Download, Calendar, ChevronDown } from "lucide-react";

const H = { fontFamily: "'Cabinet Grotesk', sans-serif" };
const CATS = ["All", "Rent", "Utilities", "Software", "Equipment", "Supplies", "Marketing", "Salaries", "Insurance", "Travel", "Training / CPD", "Cleaning", "Tax / Accounting", "Other"];

// Date filter presets
const currentYear = new Date().getFullYear();
const DATE_FILTERS = [
  { id: "all", label: "All time" },
  { id: "this-year", label: "This year" },
  { id: "last-year", label: "Last year" },
  { id: String(currentYear), label: String(currentYear) },
  { id: String(currentYear + 1), label: String(currentYear + 1) },
  { id: "custom", label: "Custom range" },
  { id: "tax-year", label: "Tax year" },
];

// Get tax year label (06/04/YYYY to 05/04/YYYY+1)
const getTaxYearLabel = (startYear) => `06/04/${startYear} – 05/04/${startYear + 1}`;

export default function ExpensesPage() {
  const [expenses, setExpenses] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [byCategory, setByCategory] = useState({});
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("All");
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: "", category: "Other", amount: 0, date: new Date().toISOString().split("T")[0], supplier: "", description: "", payment_method: "bank-transfer", recurring: false });
  
  // Attachment state
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  // Date filter state
  const [dateFilter, setDateFilter] = useState("all");
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [customDateFrom, setCustomDateFrom] = useState("");
  const [customDateTo, setCustomDateTo] = useState("");
  const [taxYearStart, setTaxYearStart] = useState(currentYear);

  // Calculate date range based on filter
  const getDateParams = useCallback(() => {
    const params = {};
    const now = new Date();
    const thisYear = now.getFullYear();
    
    switch (dateFilter) {
      case "all":
        break;
      case "this-year":
        params.year = thisYear;
        break;
      case "last-year":
        params.year = thisYear - 1;
        break;
      case "custom":
        if (customDateFrom) params.date_from = customDateFrom;
        if (customDateTo) params.date_to = customDateTo;
        break;
      case "tax-year":
        // Tax year runs from 6th April to 5th April next year
        params.date_from = `${taxYearStart}-04-06`;
        params.date_to = `${taxYearStart + 1}-04-05`;
        break;
      default:
        // Specific year (e.g., "2025", "2026")
        if (!isNaN(Number(dateFilter))) {
          params.year = Number(dateFilter);
        }
    }
    return params;
  }, [dateFilter, customDateFrom, customDateTo, taxYearStart]);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (catFilter !== "All") params.set("category", catFilter);
    
    // Add date filter params
    const dateParams = getDateParams();
    Object.entries(dateParams).forEach(([key, value]) => {
      params.set(key, value);
    });
    
    try { 
      const { data } = await api.get(`/expenses?${params}`); 
      setExpenses(data.expenses); 
      setTotalAmount(data.total_amount); 
      setByCategory(data.by_category); 
    } catch {}
    setLoading(false);
  }, [search, catFilter, getDateParams]);

  useEffect(() => { load(); }, [load]);

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please upload a PDF, JPG, or PNG file');
      return;
    }
    
    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }
    
    setAttachmentFile(file);
    setAttachmentPreview({
      name: file.name,
      type: file.type,
      size: file.size,
    });
  };

  // Remove attachment before save
  const removeAttachment = () => {
    setAttachmentFile(null);
    setAttachmentPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Get file icon based on type
  const getFileIcon = (contentType) => {
    if (contentType?.includes('pdf')) return '📄';
    if (contentType?.includes('image')) return '🖼️';
    return '📎';
  };

  const handleCreate = async (e) => { 
    e.preventDefault();
    setUploading(true);
    
    try {
      if (attachmentFile) {
        // Use FormData for multipart upload with attachment
        const formData = new FormData();
        formData.append('title', form.title);
        formData.append('category', form.category);
        formData.append('amount', form.amount);
        formData.append('date', form.date);
        formData.append('supplier', form.supplier || '');
        formData.append('description', form.description || '');
        formData.append('payment_method', form.payment_method || '');
        formData.append('recurring', form.recurring);
        formData.append('attachment', attachmentFile);
        
        await api.post("/expenses/with-attachment", formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        // Regular JSON post without attachment
        await api.post("/expenses", form);
      }
      
      setShowCreate(false);
      setForm({ title: "", category: "Other", amount: 0, date: new Date().toISOString().split("T")[0], supplier: "", description: "", payment_method: "bank-transfer", recurring: false });
      removeAttachment();
      load();
    } catch (err) {
      console.error('Failed to create expense:', err);
      alert('Failed to save expense. Please try again.');
    }
    setUploading(false);
  };

  // View/download attachment
  const viewAttachment = async (expense) => {
    if (!expense.attachment_id) return;
    try {
      const { data } = await api.get(`/files/${expense.attachment_id}`);
      if (data.data) {
        const byteString = atob(data.data);
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i);
        }
        const blob = new Blob([ab], { type: data.content_type });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
      }
    } catch (err) {
      console.error('Failed to view attachment:', err);
    }
  };

  const downloadAttachment = async (expense) => {
    if (!expense.attachment_id) return;
    try {
      const { data } = await api.get(`/files/${expense.attachment_id}`);
      if (data.data) {
        const byteString = atob(data.data);
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i);
        }
        const blob = new Blob([ab], { type: data.content_type });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename || 'attachment';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Failed to download attachment:', err);
    }
  };

  // Get current filter label
  const getCurrentFilterLabel = () => {
    if (dateFilter === "tax-year") return getTaxYearLabel(taxYearStart);
    if (dateFilter === "custom" && customDateFrom && customDateTo) {
      return `${customDateFrom} – ${customDateTo}`;
    }
    return DATE_FILTERS.find(f => f.id === dateFilter)?.label || "All time";
  };

  return (
    <div className="p-6 md:p-8 max-w-7xl" data-testid="expenses-page">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-medium text-[#1A1B1A]" style={H}>Expenses</h1><p className="text-sm text-[#757675]">{expenses.length} expenses · Total: £{totalAmount.toFixed(2)}</p></div>
        <button onClick={() => setShowCreate(true)} data-testid="add-expense-btn" className="bg-[#205B5A] text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-[#174544] flex items-center gap-1.5"><Plus className="w-4 h-4" /> Add expense</button>
      </div>

      {/* Category summary */}
      {Object.keys(byCategory).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-5">
          {Object.entries(byCategory).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([cat, amt]) => (
            <div key={cat} className="bg-white rounded-xl border border-[#1A1B1A]/[0.06] p-3">
              <p className="text-[10px] text-[#757675] mb-0.5 truncate">{cat}</p>
              <p className="text-sm font-semibold text-[#1A1B1A]">£{amt.toFixed(0)}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters row */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-[180px]"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#757675]" /><input value={search} onChange={e => setSearch(e.target.value)} data-testid="expense-search" className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 bg-white text-sm focus:outline-none focus:border-[#205B5A]" placeholder="Search expenses..." /></div>
        <select value={catFilter} onChange={e => setCatFilter(e.target.value)} data-testid="category-filter" className="px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm bg-white">{CATS.map(c => <option key={c}>{c}</option>)}</select>
        
        {/* Date filter dropdown */}
        <div className="relative">
          <button 
            onClick={() => setShowDatePicker(!showDatePicker)}
            data-testid="date-filter-btn"
            className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 bg-white text-sm hover:border-[#205B5A] transition-colors"
          >
            <Calendar className="w-3.5 h-3.5 text-[#757675]" />
            <span className="text-[#1A1B1A]">{getCurrentFilterLabel()}</span>
            <ChevronDown className="w-3.5 h-3.5 text-[#757675]" />
          </button>
          
          {showDatePicker && (
            <div className="absolute top-full mt-1 right-0 z-20 bg-white rounded-xl shadow-lg border border-[#1A1B1A]/10 py-1 min-w-[200px]" data-testid="date-filter-dropdown">
              {DATE_FILTERS.map(f => (
                <button
                  key={f.id}
                  onClick={() => {
                    if (f.id !== "custom" && f.id !== "tax-year") {
                      setDateFilter(f.id);
                      setShowDatePicker(false);
                    } else {
                      setDateFilter(f.id);
                    }
                  }}
                  data-testid={`date-filter-${f.id}`}
                  className={`w-full px-4 py-2 text-left text-sm hover:bg-[#FAF9F6] transition-colors ${dateFilter === f.id ? 'text-[#205B5A] font-medium bg-[#205B5A]/5' : 'text-[#1A1B1A]'}`}
                >
                  {f.label}
                </button>
              ))}
              
              {/* Custom date range inputs */}
              {dateFilter === "custom" && (
                <div className="px-4 py-3 border-t border-[#1A1B1A]/[0.06] space-y-2">
                  <div>
                    <label className="text-[10px] text-[#757675] uppercase">From</label>
                    <input 
                      type="date" 
                      value={customDateFrom} 
                      onChange={e => setCustomDateFrom(e.target.value)}
                      data-testid="custom-date-from"
                      className="w-full px-2 py-1.5 rounded-lg border border-[#1A1B1A]/10 text-sm mt-0.5"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-[#757675] uppercase">To</label>
                    <input 
                      type="date" 
                      value={customDateTo} 
                      onChange={e => setCustomDateTo(e.target.value)}
                      data-testid="custom-date-to"
                      className="w-full px-2 py-1.5 rounded-lg border border-[#1A1B1A]/10 text-sm mt-0.5"
                    />
                  </div>
                  <button 
                    onClick={() => setShowDatePicker(false)}
                    className="w-full mt-1 py-1.5 text-xs font-medium text-[#205B5A] bg-[#205B5A]/10 rounded-lg hover:bg-[#205B5A]/20"
                  >
                    Apply
                  </button>
                </div>
              )}
              
              {/* Tax year selector */}
              {dateFilter === "tax-year" && (
                <div className="px-4 py-3 border-t border-[#1A1B1A]/[0.06] space-y-2">
                  <label className="text-[10px] text-[#757675] uppercase">Tax Year Starting</label>
                  <select 
                    value={taxYearStart}
                    onChange={e => setTaxYearStart(Number(e.target.value))}
                    data-testid="tax-year-select"
                    className="w-full px-2 py-1.5 rounded-lg border border-[#1A1B1A]/10 text-sm"
                  >
                    {[currentYear - 2, currentYear - 1, currentYear, currentYear + 1].map(yr => (
                      <option key={yr} value={yr}>{getTaxYearLabel(yr)}</option>
                    ))}
                  </select>
                  <button 
                    onClick={() => setShowDatePicker(false)}
                    className="w-full mt-1 py-1.5 text-xs font-medium text-[#205B5A] bg-[#205B5A]/10 rounded-lg hover:bg-[#205B5A]/20"
                  >
                    Apply
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] overflow-hidden">
        <table className="w-full"><thead><tr className="border-b border-[#1A1B1A]/[0.06]">
          <th className="text-left px-5 py-3 text-[10px] font-medium text-[#757675] uppercase">Expense</th>
          <th className="text-left px-5 py-3 text-[10px] font-medium text-[#757675] uppercase hidden md:table-cell">Category</th>
          <th className="text-left px-5 py-3 text-[10px] font-medium text-[#757675] uppercase hidden md:table-cell">Date</th>
          <th className="text-left px-5 py-3 text-[10px] font-medium text-[#757675] uppercase hidden lg:table-cell">Supplier</th>
          <th className="text-center px-5 py-3 text-[10px] font-medium text-[#757675] uppercase w-16">File</th>
          <th className="text-right px-5 py-3 text-[10px] font-medium text-[#757675] uppercase">Amount</th>
        </tr></thead><tbody>
          {expenses.map(e => (
            <tr key={e.id} className="border-b border-[#1A1B1A]/[0.03] hover:bg-[#FAF9F6]" data-testid={`expense-row-${e.id}`}>
              <td className="px-5 py-3"><div className="flex items-center gap-2.5"><TrendingDown className="w-4 h-4 text-[#C4553A]" /><div><p className="text-sm font-medium text-[#1A1B1A]">{e.title}</p>{e.description && <p className="text-[10px] text-[#757675] line-clamp-1">{e.description}</p>}</div></div></td>
              <td className="px-5 py-3 text-sm text-[#757675] hidden md:table-cell">{e.category}</td>
              <td className="px-5 py-3 text-sm text-[#757675] hidden md:table-cell">{e.date}</td>
              <td className="px-5 py-3 text-sm text-[#757675] hidden lg:table-cell">{e.supplier || "—"}</td>
              <td className="px-5 py-3 text-center">
                {e.attachment_id ? (
                  <div className="inline-flex items-center gap-1">
                    <button 
                      onClick={() => viewAttachment(e)} 
                      title="View attachment"
                      data-testid={`view-attachment-${e.id}`}
                      className="p-1.5 rounded-lg hover:bg-[#205B5A]/10 text-[#205B5A] transition-colors"
                    >
                      <Eye className="w-3.5 h-3.5" />
                    </button>
                    <button 
                      onClick={() => downloadAttachment(e)} 
                      title="Download attachment"
                      data-testid={`download-attachment-${e.id}`}
                      className="p-1.5 rounded-lg hover:bg-[#205B5A]/10 text-[#205B5A] transition-colors"
                    >
                      <Download className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <span className="text-[#CACBCA]">—</span>
                )}
              </td>
              <td className="px-5 py-3 text-sm font-semibold text-[#C4553A] text-right">£{e.amount?.toFixed(2)}</td>
            </tr>
          ))}
          {expenses.length === 0 && !loading && <tr><td colSpan={6} className="px-5 py-12 text-center text-sm text-[#757675]">No expenses recorded yet</td></tr>}
        </tbody></table>
      </div>

      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4" data-testid="create-expense-modal" onClick={(e) => e.target === e.currentTarget && setShowCreate(false)}><div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
          <h2 className="text-lg font-medium text-[#1A1B1A] mb-4" style={H}>Record expense</h2>
          <form onSubmit={handleCreate} className="space-y-3">
            <input value={form.title} onChange={e => setForm({...form, title: e.target.value})} required placeholder="Expense title" data-testid="expense-title" className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]" />
            <div className="grid grid-cols-2 gap-3">
              <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm">{CATS.slice(1).map(c => <option key={c}>{c}</option>)}</select>
              <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#757675]">£</span><input type="number" step="0.01" value={form.amount} onChange={e => setForm({...form, amount: Number(e.target.value)})} required data-testid="expense-amount" className="w-full pl-7 pr-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm text-right" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} required className="px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm" />
              <input value={form.supplier} onChange={e => setForm({...form, supplier: e.target.value})} placeholder="Supplier" className="px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm" />
            </div>
            <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={2} placeholder="Description" className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm resize-none" />
            
            {/* File attachment section */}
            <div className="border border-dashed border-[#1A1B1A]/15 rounded-xl p-3">
              <input 
                type="file" 
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".pdf,.jpg,.jpeg,.png"
                className="hidden"
                data-testid="expense-file-input"
              />
              
              {!attachmentPreview ? (
                <button 
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="upload-attachment-btn"
                  className="w-full flex items-center justify-center gap-2 py-2 text-sm text-[#757675] hover:text-[#205B5A] transition-colors"
                >
                  <Upload className="w-4 h-4" />
                  <span>Attach receipt or invoice (PDF, JPG, PNG)</span>
                </button>
              ) : (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#FAF9F6] flex items-center justify-center text-lg">
                    {getFileIcon(attachmentPreview.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#1A1B1A] truncate">{attachmentPreview.name}</p>
                    <p className="text-[10px] text-[#757675]">{formatFileSize(attachmentPreview.size)}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button 
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      title="Replace file"
                      data-testid="replace-attachment-btn"
                      className="p-1.5 rounded-lg hover:bg-[#1A1B1A]/5 text-[#757675] transition-colors"
                    >
                      <Upload className="w-4 h-4" />
                    </button>
                    <button 
                      type="button"
                      onClick={removeAttachment}
                      title="Remove file"
                      data-testid="remove-attachment-btn"
                      className="p-1.5 rounded-lg hover:bg-red-50 text-[#757675] hover:text-red-500 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
            
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.recurring} onChange={e => setForm({...form, recurring: e.target.checked})} className="rounded border-[#1A1B1A]/20 text-[#205B5A]" /><span className="text-xs text-[#4A4B4A]">Recurring expense</span></label>
            <div className="flex justify-end gap-3 pt-1">
              <button type="button" onClick={() => { setShowCreate(false); removeAttachment(); }} className="px-4 py-2 text-sm text-[#757675]">Cancel</button>
              <button type="submit" disabled={uploading} data-testid="save-expense-btn" className="bg-[#205B5A] text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-[#174544] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                {uploading && <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                {uploading ? 'Saving...' : 'Save expense'}
              </button>
            </div>
          </form>
        </div></div>
      )}

      {/* Click outside to close date picker */}
      {showDatePicker && (
        <div className="fixed inset-0 z-10" onClick={() => setShowDatePicker(false)} />
      )}
    </div>
  );
}
